/**
 * Financeurs et modes de financement (décision du dirigeant, 04/11/2026).
 *
 * Seuls financeurs retenus : CPF (formation ET bilan de compétences), FAFCEA, FIFPL, puis les OPCO. Les autres modes
 * (« FAF » générique, « entreprise », « fonds propres ») sont RETIRÉS des listes de choix ; un dossier qui en porte un le
 * garde et l'affiche « (ancien mode) » — aucune donnée n'est réécrite.
 *
 * Valeurs de départ des fiches financeurs : uniquement ce qui a été VÉRIFIÉ sur une source citée ; le reste est vide
 * (« à compléter » à l'écran). Les délais réglementaires sont des PARAMÈTRES de la fiche, jamais du code.
 */

export const MODES_RETENUS = ["cpf_formation", "cpf_bilan", "fafcea", "fifpl", "opco"] as const;
export const MODES_ANCIENS = ["faf", "entreprise", "fonds_propres"] as const;
export type ModeRetenu = (typeof MODES_RETENUS)[number];
export type ModeAncien = (typeof MODES_ANCIENS)[number];
export type ModeFinancement = ModeRetenu | ModeAncien;
export const TOUS_LES_MODES = [...MODES_RETENUS, ...MODES_ANCIENS] as const;

/** Un financeur porte le même identifiant que le mode de financement retenu correspondant. */
export type Financeur = ModeRetenu;
export const FINANCEURS = MODES_RETENUS;

export const LIBELLES_MODES: Record<ModeFinancement, string> = {
  cpf_formation: "CPF — formation",
  cpf_bilan: "CPF — bilan de compétences",
  fafcea: "FAFCEA",
  fifpl: "FIFPL",
  opco: "OPCO",
  faf: "FAF (travailleur indépendant)",
  entreprise: "Entreprise (fonds propres)",
  fonds_propres: "Particulier / fonds propres",
};

export const estModeRetenu = (m: string): m is ModeRetenu => (MODES_RETENUS as readonly string[]).includes(m);
export const estModeConnu = (m: string): m is ModeFinancement =>
  (TOUS_LES_MODES as readonly string[]).includes(m);

/** Libellé affiché : un mode retiré des choix reste lisible, suivi de « (ancien mode) ». */
export function libelleMode(m: string): string {
  if (!estModeConnu(m)) return m;
  return estModeRetenu(m) ? LIBELLES_MODES[m] : `${LIBELLES_MODES[m]} (ancien mode)`;
}

/** Les choix proposés à la création ou à la modification : modes retenus, plus le mode actuel s'il est ancien. */
export function choixModes(
  actuel?: string,
): Array<{ valeur: ModeFinancement; libelle: string; ancien: boolean }> {
  const l: Array<{ valeur: ModeFinancement; libelle: string; ancien: boolean }> = MODES_RETENUS.map((m) => ({
    valeur: m,
    libelle: LIBELLES_MODES[m],
    ancien: false,
  }));
  if (actuel && estModeConnu(actuel) && !estModeRetenu(actuel))
    l.push({ valeur: actuel, libelle: libelleMode(actuel), ancien: true });
  return l;
}

export const estCpf = (f: string): f is "cpf_formation" | "cpf_bilan" =>
  f === "cpf_formation" || f === "cpf_bilan";
export const famille = (f: Financeur): "cpf" | "classique" => (estCpf(f) ? "cpf" : "classique");

/** Mode qui ouvre droit à un opérateur de compétences (champ « OPCO » du dossier, clause de subrogation). */
export const avecOpco = (m: string) => m === "opco" || m === "faf";

// ——— OPCO ———

/**
 * Les 11 opérateurs de compétences agréés (arrêtés publiés au Journal officiel du 29/03/2019, JORFCONT000038318056 ;
 * liste relayée par Centre Inffo : https://www.centre-inffo.fr/site-reforme/la-liste-des-11-opco-publiee-au-journal-officiel).
 * Dénomination de l'agrément, puis nom d'usage. La page du ministère du Travail (https://travail-emploi.gouv.fr/les-operateurs-de-competences-opco)
 * n'a pas pu être relue le 04/11/2026 : liste À REVÉRIFIER avant mise en production (une fusion ou un retrait d'agrément
 * se corrige ici, une ligne).
 */
export const OPCO_AGREES = [
  { nom: "OPCO Commerce", agrement: "Opco Commerce" },
  { nom: "Atlas", agrement: "Atlas" },
  { nom: "OPCO Santé", agrement: "Santé" },
  { nom: "AFDAS", agrement: "AFDAS" },
  { nom: "Uniformation", agrement: "Cohésion sociale" },
  { nom: "OPCO EP", agrement: "Entreprises de proximité" },
  { nom: "Akto", agrement: "Entreprises et salariés des services à forte intensité de main-d'œuvre" },
  { nom: "OCAPIAT", agrement: "OCAPIAT" },
  { nom: "OPCO 2i", agrement: "OPCO 2i" },
  { nom: "Constructys", agrement: "Construction" },
  { nom: "OPCO Mobilités", agrement: "Mobilité" },
] as const;

// ——— Fiches financeurs : valeurs de départ vérifiées ———

export type Canal = "portail" | "email" | "formulaire";
export const LIBELLES_CANAUX: Record<Canal, string> = {
  portail: "Dépôt sur le portail du financeur",
  email: "Envoi par e-mail à un interlocuteur",
  formulaire: "Formulaire PDF signé à la main, envoyé au financeur",
};

export interface PieceExigee {
  code: string;
  libelle: string;
  /** « avant » : avec la demande ; « apres » : justificatifs de fin de formation. */
  phase: "avant" | "apres";
  /** Pièce du dossier de formation qui y répond (cochée automatiquement si elle est présente). */
  piece_dossier?: string;
  obligatoire: boolean;
}

export interface Source {
  titre: string;
  url: string;
  consulte_le: string;
}

export interface FicheFinanceur {
  financeur: Financeur;
  /** Canaux possibles ; le premier est proposé par défaut à chaque demande. */
  canaux: Canal[];
  portail_url: string;
  email_interlocuteur: string;
  /** Dépôt possible au plus tôt N jours avant le début (null = à compléter). */
  depot_ouverture_jours_avant: number | null;
  /** Dépôt au plus tard N jours avant le début ; 0 = le jour du début ; négatif = après le début (null = à compléter). */
  depot_limite_jours_avant: number | null;
  /** Justificatifs de fin à envoyer au plus tard N jours après la fin (null = à compléter). */
  justificatifs_jours_apres_fin: number | null;
  /** Rappel interne : relance N jours après le dépôt sans réponse. */
  relance_jours: number;
  /** Paiement direct à l'organisme (subrogation) ; FAFCEA : non — il règle l'entreprise. */
  subrogation: boolean | null;
  pieces: PieceExigee[];
  regles: string[];
  sources: Source[];
}

const NOTICE_FAFCEA: Source = {
  titre: "FAFCEA — Notice « Demande individuelle 2026 » (fournie par le dirigeant le 04/11/2026)",
  url: "https://mon-entreprise.fafcea.com",
  consulte_le: "2026-11-04",
};

export const FICHES_DE_DEPART: Record<Financeur, FicheFinanceur> = {
  fafcea: {
    financeur: "fafcea",
    canaux: ["formulaire", "portail"],
    portail_url: "https://mon-entreprise.fafcea.com",
    email_interlocuteur: "",
    // « au plus tôt 3 mois avant et au plus tard le jour du début » (date de RÉCEPTION par le FAFCEA) : 3 mois ≈ 92 jours,
    // l'échéance exacte est calculée en mois calendaires (domaine/financements/suivi.ts).
    depot_ouverture_jours_avant: 92,
    depot_limite_jours_avant: 0,
    // « au plus tard 2 mois après la fin du stage » : ≈ 61 jours, calcul en mois calendaires.
    justificatifs_jours_apres_fin: 61,
    relance_jours: 15,
    subrogation: false,
    pieces: [
      {
        code: "rne",
        libelle:
          "Extrait d'inscription au registre national des entreprises de moins d'un an au début de la formation (data.inpi.fr)",
        phase: "avant",
        obligatoire: true,
      },
      {
        code: "urssaf",
        libelle:
          "Attestation URSSAF de contribution à la formation de l'année (ou N-1 au 1er trimestre) — une attestation à 0 € n'ouvre pas de droit",
        phase: "avant",
        obligatoire: true,
      },
      {
        code: "formulaire",
        libelle:
          "Formulaire de demande de prise en charge complété et signé par le stagiaire (sauf dépôt sur le portail)",
        phase: "avant",
        obligatoire: true,
      },
      {
        code: "programme",
        libelle: "Programme pédagogique détaillé",
        phase: "avant",
        piece_dossier: "PRG",
        obligatoire: true,
      },
      {
        code: "motivation",
        libelle:
          "Lettre de motivation et test de positionnement (formations examinées en commission technique)",
        phase: "avant",
        obligatoire: false,
      },
      {
        code: "calendrier",
        libelle: "Calendrier détaillé avec horaires (jours discontinus)",
        phase: "avant",
        piece_dossier: "03-AVT",
        obligatoire: false,
      },
      {
        code: "facture",
        libelle: "Facture acquittée, avec cachet et signature de l'organisme de formation",
        phase: "apres",
        piece_dossier: "11-FIN",
        obligatoire: true,
      },
      {
        code: "presence",
        libelle:
          "Attestation de présence OU certificat de réalisation OU émargement par demi-journée (obligatoire au-delà de 100 h)",
        phase: "apres",
        piece_dossier: "06-PDT",
        obligatoire: true,
      },
      {
        code: "distance",
        libelle:
          "À distance : attestation d'assiduité ou certificat de réalisation, relevé de connexion possible, attestation sur l'honneur du stagiaire",
        phase: "apres",
        obligatoire: false,
      },
    ],
    regles: [
      "Dossier complet en un seul envoi ; c'est la date de réception par le FAFCEA qui compte.",
      "Aucune formation ne peut être reportée d'un exercice à l'autre ; contenu identique à celui soumis à l'agrément.",
      "Pas de subrogation de paiement : le FAFCEA règle directement l'entreprise, sur le montant hors taxe.",
      "Tout changement de dates ou de lieu est signalé AVANT les dates initiales, sinon l'agrément est annulé.",
      "Justificatifs de fin : un seul envoi, avec la notification de décision.",
      "Le formulaire change chaque année : téléverser celui de l'année en cours.",
    ],
    sources: [NOTICE_FAFCEA],
  },
  fifpl: {
    financeur: "fifpl",
    canaux: ["portail"],
    // Espace adhérent cité par fifpl.fr (lien « Mon espace adhérent ») ; dépôt en ligne, pièces jointes en PDF.
    portail_url: "https://adherent.fifpl.fr",
    email_interlocuteur: "",
    depot_ouverture_jours_avant: null,
    // Délai à compléter : une source secondaire (PGA France) indique « au plus tard 10 jours après la formation » ;
    // non confirmé sur fifpl.fr le 04/11/2026 → laissé vide.
    depot_limite_jours_avant: null,
    justificatifs_jours_apres_fin: null,
    relance_jours: 15,
    subrogation: null,
    pieces: [
      {
        code: "programme",
        libelle: "Programme de la formation",
        phase: "avant",
        piece_dossier: "PRG",
        obligatoire: true,
      },
      {
        code: "convention",
        libelle: "Convention de formation",
        phase: "avant",
        piece_dossier: "02-AVT",
        obligatoire: true,
      },
      {
        code: "urssaf",
        libelle: "Attestation URSSAF de contribution à la formation professionnelle",
        phase: "avant",
        obligatoire: true,
      },
      { code: "rib", libelle: "RIB du professionnel", phase: "avant", obligatoire: true },
      { code: "facture", libelle: "Facture", phase: "apres", piece_dossier: "11-FIN", obligatoire: true },
      {
        code: "presence",
        libelle: "Attestation de présence signée",
        phase: "apres",
        piece_dossier: "09-FIN",
        obligatoire: true,
      },
    ],
    regles: [
      "Demande en ligne sur l'espace adhérent ; pièces jointes en PDF.",
      "Critères de prise en charge propres à chaque profession (code NAF) : vérifier ceux de l'année sur fifpl.fr.",
    ],
    sources: [
      {
        titre: "FIF PL — site officiel (espace adhérent, critères 2026 par code NAF)",
        url: "https://www.fifpl.fr/sites/default/files/documents/criteres/6910ZH.pdf",
        consulte_le: "2026-11-04",
      },
      {
        titre: "PGA France — procédure de prise en charge FIF PL (source secondaire, à confirmer)",
        url: "https://www.pgafrance.org/models/gallerymedia/assets/d//d4acd3598d606_procedure-prise-en-charge-fif-pl.pdf",
        consulte_le: "2026-11-04",
      },
    ],
  },
  opco: {
    financeur: "opco",
    canaux: ["portail", "email"],
    portail_url: "",
    email_interlocuteur: "",
    depot_ouverture_jours_avant: null,
    depot_limite_jours_avant: null,
    justificatifs_jours_apres_fin: null,
    relance_jours: 15,
    subrogation: null,
    pieces: [
      {
        code: "convention",
        libelle: "Convention de formation signée",
        phase: "avant",
        piece_dossier: "02-AVT",
        obligatoire: true,
      },
      {
        code: "programme",
        libelle: "Programme de formation",
        phase: "avant",
        piece_dossier: "PRG",
        obligatoire: true,
      },
      { code: "facture", libelle: "Facture", phase: "apres", piece_dossier: "11-FIN", obligatoire: true },
      {
        code: "presence",
        libelle: "Émargement / certificat de réalisation",
        phase: "apres",
        piece_dossier: "06-PDT",
        obligatoire: true,
      },
    ],
    regles: [
      "Chaque OPCO a son portail, ses délais et ses critères (branche, taille d'entreprise) : à compléter OPCO par OPCO.",
    ],
    sources: [
      {
        titre: "Centre Inffo — la liste des 11 OPCO publiée au Journal officiel (29/03/2019)",
        url: "https://www.centre-inffo.fr/site-reforme/la-liste-des-11-opco-publiee-au-journal-officiel",
        consulte_le: "2026-11-04",
      },
    ],
  },
  cpf_formation: {
    financeur: "cpf_formation",
    canaux: ["portail"],
    portail_url: "https://of.moncompteformation.gouv.fr",
    email_interlocuteur: "",
    depot_ouverture_jours_avant: null,
    depot_limite_jours_avant: null,
    justificatifs_jours_apres_fin: null,
    relance_jours: 2,
    subrogation: true,
    pieces: [
      {
        code: "offre",
        libelle: "Offre publiée sur EDOF (formation → action → session) correspondant au dossier",
        phase: "avant",
        obligatoire: true,
      },
      {
        code: "programme",
        libelle: "Programme de formation",
        phase: "avant",
        piece_dossier: "PRG",
        obligatoire: true,
      },
      {
        code: "presence",
        libelle: "Émargement (preuve de l'entrée et de la réalisation)",
        phase: "apres",
        piece_dossier: "06-PDT",
        obligatoire: true,
      },
      {
        code: "certificat",
        libelle: "Certificat de réalisation / attestation de fin",
        phase: "apres",
        piece_dossier: "09-FIN",
        obligatoire: true,
      },
      {
        code: "export_edof",
        libelle: "Export PDF du dossier EDOF (« Exporter le dossier »)",
        phase: "apres",
        obligatoire: false,
      },
    ],
    regles: [
      "Pas d'API : l'inscription est faite par le titulaire sur Mon Compte Formation ; l'organisme répond, déclare l'entrée, la sortie et le service fait dans EDOF, puis facture la Caisse des Dépôts. Le SaaS suit et rappelle ; on recopie numéros et dates.",
      "Délais des conditions générales d'utilisation (réponse de l'organisme, entrée en formation, rétractation, déclarations) : à saisir dans les paramètres après lecture des CGU en vigueur.",
      "Participation forfaitaire du titulaire : aucun montant n'est calculé par le SaaS.",
    ],
    sources: [
      {
        titre: "Centre Inffo — Mon Compte Formation : version 15 des CGU (05/05/2026)",
        url: "https://www.centre-inffo.fr/site-droit-formation/actualites-droit/mon-compte-formation-une-version-15-des-cgu-sous-le-signe-du-controle-et-de-la-tracabilite",
        consulte_le: "2026-11-04",
      },
      {
        titre:
          "EDOF — espace des organismes (circuit et statuts relevés dans l'espace de Formatrix, lecture seule, 04/11/2026)",
        url: "https://of.moncompteformation.gouv.fr/espace-prive/html/#/dossiers",
        consulte_le: "2026-11-04",
      },
    ],
  },
  cpf_bilan: {
    financeur: "cpf_bilan",
    canaux: ["portail"],
    portail_url: "https://of.moncompteformation.gouv.fr",
    email_interlocuteur: "",
    depot_ouverture_jours_avant: null,
    depot_limite_jours_avant: null,
    justificatifs_jours_apres_fin: null,
    relance_jours: 2,
    subrogation: true,
    pieces: [
      {
        code: "offre",
        libelle: "Offre « Bilan de compétences » publiée sur EDOF",
        phase: "avant",
        obligatoire: true,
      },
      {
        code: "consentement",
        libelle: "Consentement du bénéficiaire (art. L6313-4)",
        phase: "avant",
        obligatoire: true,
      },
      {
        code: "synthese",
        libelle: "Document de synthèse remis au SEUL bénéficiaire (non transmis à un tiers sans son accord)",
        phase: "apres",
        obligatoire: true,
      },
      {
        code: "presence",
        libelle: "Émargement des entretiens",
        phase: "apres",
        piece_dossier: "06-PDT",
        obligatoire: true,
      },
      { code: "export_edof", libelle: "Export PDF du dossier EDOF", phase: "apres", obligatoire: false },
    ],
    regles: [
      "Durée : 24 heures au plus par bilan (art. L6313-4).",
      "Trois phases : préliminaire, investigation, conclusions ; la phase de conclusions prévoit la possibilité d'un entretien de suivi (art. R6313-4).",
      "Réalisé seulement avec le consentement du bénéficiaire ; résultats détaillés et document de synthèse communiqués au bénéficiaire, à un tiers seulement avec son accord ; secret professionnel (art. L6313-4).",
    ],
    sources: [
      {
        titre: "Code du travail, art. L6313-4",
        url: "https://code.travail.gouv.fr/code-du-travail/l6313-4",
        consulte_le: "2026-11-04",
      },
      {
        titre: "Code du travail, art. R6313-4",
        url: "https://code.travail.gouv.fr/code-du-travail/r6313-4",
        consulte_le: "2026-11-04",
      },
      {
        titre:
          "Code du travail, art. R6313-8 (convention tripartite, 10 jours pour accepter — plan de développement des compétences)",
        url: "https://code.travail.gouv.fr/code-du-travail/r6313-8",
        consulte_le: "2026-11-04",
      },
    ],
  },
};

/** Financeurs activés par défaut : CPF (formation et bilan) partout ; FAFCEA, FIFPL, OPCO pour les nouveaux organismes. */
export function activationParDefaut(f: Financeur, organismeParDefaut: boolean): boolean {
  return estCpf(f) || !organismeParDefaut;
}
